import { clsx } from 'clsx';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import NumberField from '../NumberField';
import Field from '@/components/common/Field';

type Unit = 'hours' | 'minutes' | 'seconds';

export interface DurationFieldProps {
    name: string; // base name, internal hidden target field (e.g., seconds total)
    units: Unit[]; // order of fields to render
    label?: string;
    className?: string;
    // Total target unit to compute into: 'seconds' | 'minutes' | 'hours'
    target?: Unit;
}

const unitToLabelKey: Record<Unit, string> = {
    hours: 'hr',
    minutes: 'min',
    seconds: 'sec',
};

const toSeconds = (h?: number | null, m?: number | null, s?: number | null): number => {
    const hh = h ?? 0;
    const mm = m ?? 0;
    const ss = s ?? 0;
    return hh * 3600 + mm * 60 + ss;
};

const toMinutes = (secTotal: number): number => Math.floor(secTotal / 60);
const toHours = (secTotal: number): number => Math.floor(secTotal / 3600);

const DurationField = ({ name, units, label, className, target = 'seconds' }: DurationFieldProps) => {
    const { watch, setValue, trigger, getFieldState, formState } = useFormContext();

    const [hName, mName, sName] = useMemo(() => {
        const parts: (string | undefined)[] = [];
        parts[0] = units.includes('hours') ? `${name}Hours` : undefined;
        parts[1] = units.includes('minutes') ? `${name}Minutes` : undefined;
        parts[2] = units.includes('seconds') ? `${name}Seconds` : undefined;
        return parts as [string | undefined, string | undefined, string | undefined];
    }, [name, units]);

    const hours = hName ? watch(hName) : null;
    const minutes = mName ? watch(mName) : null;
    const seconds = sName ? watch(sName) : null;

    const fieldState = getFieldState(name, formState);

    // Compute derived total into base field whenever any piece changes
    const computeAndSet = () => {
        const rawH: any = hours;
        const rawM: any = minutes;
        const rawS: any = seconds;

        const isEmpty = (v: any) => v === null || v === undefined || v === '';
        const allEmpty = isEmpty(rawH) && isEmpty(rawM) && isEmpty(rawS);

        if (allEmpty) {
            setValue(name as any, null);
            return;
        }

        const toSafe = (v: any) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);
        const totalSec = toSeconds(toSafe(rawH), toSafe(rawM), toSafe(rawS));

        let targetValue: number = totalSec;
        if (target === 'minutes') {
            targetValue = toMinutes(totalSec);
        }
        if (target === 'hours') {
            targetValue = toHours(totalSec);
        }
        setValue(name as any, targetValue);
        trigger(name as any);
    };

    useEffect(() => {
        computeAndSet();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hours, minutes, seconds, target]);

    return (
        <Field
            name={name}
            id={name}
            label={label}
            errorElement={fieldState.error?.message as any}
            className={clsx('w-full', className)}
        >
            <div className="flex items-center gap-2">
                {hName && <NumberField name={hName} min={0} suffixElement={unitToLabelKey.hours} />}
                {hName && (mName || sName) && <span className="text-gray-500">:</span>}
                {mName && <NumberField name={mName} min={0} max={59} suffixElement={unitToLabelKey.minutes} />}
                {mName && sName && <span className="text-gray-500">:</span>}
                {sName && <NumberField name={sName} min={0} max={59} suffixElement={unitToLabelKey.seconds} />}
            </div>
        </Field>
    );
};

export default DurationField;
